import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Button, Card, Group, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { useT } from '../../i18n/I18nProvider';
import { NAV } from '../../lib/nav';
import './not-found.css';

/**
 * The 404.
 *
 * Per `_UI_improvements/tenant/not-found.md`:
 * - the route already sits OUTSIDE the authenticated group, so a signed-out
 *   visitor following a bad link now reaches this page rather than being
 *   bounced to the login form with no explanation (§4 item 1, fixed with the
 *   shell work);
 * - the attempted URL is shown, so the user can see whether they mistyped and
 *   support has something to work with (§4 item 2);
 * - "Back to dashboard" was the only way out (§4 item 3);
 * - it used a bare left-aligned `Stack` where `EmptyState` was right there,
 *   and an `<h2>` with no `<h1>` anywhere on the page (§4 items 5, 6, 8).
 */
export default function NotFoundPage() {
  const t = useT();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();

  // A signed-out visitor cannot be offered the app's own destinations.
  const signedIn = !!user && user.scope === 'tenant';

  /*
   * "Did you mean": the closest nav entry by path prefix, which catches the
   * common cases (/contact → /contacts, /inbox/ → /inbox) without pulling in a
   * fuzzy-match library for one screen.
   */
  const suggestion = (() => {
    if (!signedIn) return null;
    const target = pathname.toLowerCase().replace(/\/+$/, '');
    const items = NAV.flatMap((s) => s.items);
    return items.find((i) => target.startsWith(i.path) && i.path !== '/')
      ?? items.find((i) => i.path !== '/' && i.path.startsWith(target) && target.length > 2)
      ?? null;
  })();

  return (
    <div className="rr-notfound">
      <Card withBorder className="rr-notfound__card">
        <Stack gap="md">
          {/* The page's own h1: every other page gets one from PageHeader and
              this one had none. */}
          <Title order={1} className="rr-notfound__code">404</Title>

          <EmptyState
            title={t('notFound.title')}
            description={t('notFound.description')}
          />

          {/* §4 item 2. Rendered as text rather than a link, since following it
              again would only land back here. */}
          <div className="rr-notfound__path">
            <Text role="caption" c="var(--rr-color-text-muted)">{t('notFound.attempted')}</Text>
            <code>{pathname}</code>
          </div>

          {suggestion && (
            <Text role="bodySmall">
              {t('notFound.didYouMean')}{' '}
              <button type="button" className="rr-notfound__suggest" onClick={() => nav(suggestion.path)}>
                {t(suggestion.labelKey, suggestion.label)}
              </button>
            </Text>
          )}

          <Group gap="xs" wrap="wrap">
            {signedIn ? (
              <>
                <Button onClick={() => nav('/')}>{t('notFound.backToDashboard')}</Button>
                <Button variant="default" onClick={() => nav('/inbox')}>{t('nav.inbox')}</Button>
                <Button variant="default" onClick={() => nav('/contacts')}>{t('nav.contacts')}</Button>
              </>
            ) : (
              <Button onClick={() => nav('/auth/login')}>{t('action.signIn')}</Button>
            )}
          </Group>
        </Stack>
      </Card>
    </div>
  );
}
